import React, { useMemo, useState, useEffect } from 'react';
import VerticalTabs from '@components/v2/VerticalTabs';
import { Loader } from '@ui';
import { translate } from '@utils/i18n';
import GrammarAndStyleTab from './GrammarAndStyleTab';
import MissingInformationTab from './MissingInformationTab';
import NeedsCorrectionTab from './NeedsCorrectionTab';
import UnansweredQuestionsTab from './UnansweredQuestionsTab';
import OfficeOnlyTab from './OfficeOnlyTab';
import useAIInspectionManager from '../../hooks/useAIInspectionManager';
import './AdvancedSettingsStyles.scss';
// Tab IDs
const GRAMMAR_AND_STYLE_TAB = 'grammar-and-style';
const MISSING_INFORMATION_TAB = 'missing-information';
const NEEDS_CORRECTION_TAB = 'needs-correction';
const UNANSWERED_QUESTIONS_TAB = 'unanswered-questions';
const OFFICE_ONLY_TAB = 'office-only';
const namespace = 'features.settings.ai_inspection_manager.advanced_settings';
function AdvancedSettings() {
    const { data: settings, isLoading } = useAIInspectionManager();
    const [activeTabId, setActiveTabId] = useState(undefined);
    const allTabs = useMemo(() => {
        var _a, _b, _c, _d, _e;
        return [
            {
                id: GRAMMAR_AND_STYLE_TAB,
                label: translate('grammar_and_style', { namespace }),
                content: React.createElement(GrammarAndStyleTab, null),
                enabled: (_a = settings === null || settings === void 0 ? void 0 : settings.aiInspectionManagerGrammarAndStyleEnabled) !== null && _a !== void 0 ? _a : true
            },
            {
                id: MISSING_INFORMATION_TAB,
                label: translate('missing_information', { namespace }),
                content: React.createElement(MissingInformationTab, null),
                enabled: (_b = settings === null || settings === void 0 ? void 0 : settings.aiInspectionManagerMissingInformationEnabled) !== null && _b !== void 0 ? _b : true
            },
            {
                id: NEEDS_CORRECTION_TAB,
                label: translate('needs_correction', { namespace }),
                content: React.createElement(NeedsCorrectionTab, null),
                enabled: (_c = settings === null || settings === void 0 ? void 0 : settings.aiInspectionManagerNeedsCorrectionEnabled) !== null && _c !== void 0 ? _c : true
            },
            {
                id: UNANSWERED_QUESTIONS_TAB,
                label: translate('unanswered_questions', { namespace }),
                content: React.createElement(UnansweredQuestionsTab, null),
                enabled: (_d = settings === null || settings === void 0 ? void 0 : settings.aiInspectionManagerUnansweredQuestionsEnabled) !== null && _d !== void 0 ? _d : true
            },
            {
                id: OFFICE_ONLY_TAB,
                label: translate('office_only', { namespace }),
                content: React.createElement(OfficeOnlyTab, null),
                enabled: (_e = settings === null || settings === void 0 ? void 0 : settings.aiInspectionManagerOfficeOnlyEnabled) !== null && _e !== void 0 ? _e : true
            }
        ];
    }, [settings]);
    const enabledTabs = useMemo(() => allTabs.filter((tab) => tab.enabled), [allTabs]);
    const firstEnabledTabId = useMemo(() => { var _a; return (_a = enabledTabs[0]) === null || _a === void 0 ? void 0 : _a.id; }, [enabledTabs]);
    // Automatically switch to first enabled tab if current tab is disabled or not set
    useEffect(() => {
        if (!activeTabId || !enabledTabs.find((tab) => tab.id === activeTabId)) {
            setActiveTabId(firstEnabledTabId);
        }
    }, [activeTabId, enabledTabs, firstEnabledTabId]);
    if (isLoading) {
        return (React.createElement("div", { className: "advanced-settings" },
            React.createElement("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' } },
                React.createElement(Loader, { size: "48", title: "Loading", vertical: true }))));
    }
    // Hide entire section if no tabs are enabled
    if (enabledTabs.length === 0) {
        return null;
    }
    return (React.createElement("div", { className: "advanced-settings" },
        React.createElement("h2", { className: "advanced-settings__title" }, translate('title', { namespace })),
        React.createElement(VerticalTabs, { tabs: enabledTabs, activeTabId: activeTabId, onChange: setActiveTabId })));
}
export default AdvancedSettings;
